<template>
  <ctk-dialog
    v-model="dialogValue"
    modal-class="shipment-proposals-accept-dialog"
    max-width="765px"
    hide-header
    hide-footer
    persistent
  >
    <!-- Bid view -->
    <shipment-proposals-accept-dialog-bid-view
      v-if="!(paymentView || accepted || paymentError)"
      :shipment="shipment"
      :proposal="proposal"
      @close="close"
      @accept="accept"
      data-test="bid-view"
    />

    <!-- Bid confirmation view -->
    <shipment-proposals-accept-dialog-bid-confirmation-view
      v-if="!needPayment && accepted"
      :shipment="shipment"
      :proposal="proposal"
      @close="close"
      data-test="bid-confirmation-view"
    />

    <!-- Payment view -->
    <shipment-proposals-accept-dialog-payment-view
      v-if="needPayment && paymentView && !paymentError && !accepted"
      :stripe="stripe"
      :shipment="shipment"
      :proposal="proposal"
      @submitted="submitted"
      @close="close"
      data-test="payment-view"
    />

    <!-- Payment confirmation -->
    <shipment-proposals-accept-dialog-payment-confirmation-view
      v-if="needPayment && accepted && !paymentError"
      :shipment="shipment"
      @close="close"
      data-test="payment-confirmation-view"
    />

    <!-- Payment error -->
    <shipment-proposals-accept-dialog-payment-error-view
      v-if="needPayment && paymentError"
      @retry="paymentError = false"
      @close="close"
      data-test="payment-error-view"
    />
  </ctk-dialog>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import { ShipmentProposal } from '@/resources'
  import * as Sentry from '@sentry/vue'

  import useStripe, { resolveNewQuotationStripeAccount } from '@/composables/useStripe'
  import useModelGetterSetter from '@/composables/useModelGetterSetter'

  import CtkDialog from '@/components/CtkDialog/index.vue'
  import ShipmentProposalsAcceptDialogBidView from './_subs/ShipmentProposalsAcceptDialogBidView/index.vue'
  import ShipmentProposalsAcceptDialogBidConfirmationView from './_subs/ShipmentProposalsAcceptDialogBidConfirmationView/index.vue'
  import ShipmentProposalsAcceptDialogPaymentView from './_subs/ShipmentProposalsAcceptDialogPaymentView/index.vue'
  import ShipmentProposalsAcceptDialogPaymentConfirmationView from './_subs/ShipmentProposalsAcceptDialogPaymentConfirmationView/index.vue'
  import ShipmentProposalsAcceptDialogPaymentErrorView from './_subs/ShipmentProposalsAcceptDialogPaymentErrorView/index.vue'
  import { showToaster } from '@/services/Toaster'

  import { EventBus } from '@/services/EventBus'

  /**
   * @module component - ShipmentProposalsAcceptDialog
   * @param {object} shipment
   * @param {object} proposal
   */
  export default {
    name: 'ShipmentProposalsAcceptDialog',
    components: {
      CtkDialog,
      ShipmentProposalsAcceptDialogBidView,
      ShipmentProposalsAcceptDialogBidConfirmationView,
      ShipmentProposalsAcceptDialogPaymentView,
      ShipmentProposalsAcceptDialogPaymentConfirmationView,
      ShipmentProposalsAcceptDialogPaymentErrorView
    },
    props: {
      value: {
        type: Boolean,
        required: true
      },
      shipment: {
        type: Object,
        required: true
      },
      proposal: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        paymentView: false,
        paymentError: false,
        accepted: false
      }
    },
    setup (props) {
      const { stripe, setStripeAccount } = useStripe()
      const { state: dialogValue } = useModelGetterSetter(props, 'value')

      return {
        stripe,
        setStripeAccount,
        dialogValue
      }
    },
    watch: {
      async value (v) {
        if (!v) {
          /**
           * Set the default values whenever the user closes the dialog
           */
          this.paymentView = false
          this.paymentError = false
          this.accepted = false

          return
        }

        await this.syncPaymentContext()
      }
    },
    computed: {
      ...mapGetters('auth', [
        'getCid'
      ]),
      needPayment () {
        return this.proposal.need_payment
      },
      loaderKey () {
        return `accepting proposal ${this.proposal.uuid}`
      }
    },
    methods: {
      ...mapActions('auth', [
        'retrieveDefaultStripeAccount',
        'retrievePaymentSources'
      ]),
      close () {
        this.dialogValue = false
      },
      async syncPaymentContext () {
        const defaultAccountRes = await Promise.resolve(this.retrieveDefaultStripeAccount())
        const stripeAccount = defaultAccountRes && defaultAccountRes.data && defaultAccountRes.data.stripe_account
          ? defaultAccountRes.data.stripe_account
          : resolveNewQuotationStripeAccount()

        await Promise.all([
          Promise.resolve(this.retrievePaymentSources({ stripeAccount })).catch(() => {}),
          Promise.resolve(this.setStripeAccount(stripeAccount)).catch(() => {})
        ])
      },
      /**
       * Either show the payment view or accept the bid right away.
       * @function accept
       */
      accept () {
        if (this.$matomo) {
          this.$matomo.trackEvent('Shipments', 'Confirmed Proposal Acceptation', this.shipment.uuid)
        }

        if (this.$wait.is(this.loaderKey)) return

        if (this.needPayment) {
          this.paymentView = true
        } else {
          this.submitted(null)
        }
      },
      /**
       * @function acceptationSuccessHandler
       */
      acceptationSuccessHandler () {
        EventBus.$emit('shipments:refresh-list')
        EventBus.$emit('shipment:refresh')

        this.accepted = true
        this.$wait.end(this.loaderKey)
      },
      /**
       * @function acceptationErrorHandler
       * @param {any} err
       */
      acceptationErrorHandler (err) {
        if (!err.response) {
          this.$wait.end(this.loaderKey)
          return
        }

        const { data } = err.response
        if (!data) {
          this.$wait.end(this.loaderKey)
          return
        }

        if (err.response.status === 402) {
          try {
            /**
             * Handle Stripe next actions if required
             */
            const paymentMethod = data.payment_method
            switch (paymentMethod.status) {
            case 'requires_auth':
              this.$wait.start(this.loaderKey)
              this.stripe.handleCardAction(paymentMethod.secret)
                .then((/** @type {any} */ cardResponse) => {
                  if (cardResponse.error) {
                    this.paymentError = true

                    this.$wait.end(this.loaderKey)
                  } else {
                    /**
                     * Got a positive response from Stripe, retry
                     * to submit the acceptation with the payment intent.
                     */
                    this.submitted({
                      id: cardResponse.paymentIntent.id,
                      type: 'stripe_payment_intent'
                    })
                  }
                })
              break
            case 'refused':
              this.paymentError = true
              this.$wait.end(this.loaderKey)
              break
            default:
              throw new Error(`Could not handle "${data.payment_method.status}" status.`)
            }
          } catch (e) {
            this.$wait.end(this.loaderKey)
            Sentry.captureException(e)
            showToaster(this, e.message, { type: 'error' })
          }
        } else {
          this.$wait.end(this.loaderKey)

          this.accepted = false

          if (data.error) {
            /**
             * Show the violation message in the input
             */
            if (data.error.violations) {
              try {
                /**
                 * TODO: Use the handlePropertyPathViolations handler.
                 */
                data.error.violations.forEach(({ property_path: property, message }) => {
                  const provider = this.$refs[`${property}-provider`]
                  if (!provider) {
                    throw new Error(`The provider for the violation "${property}" does not exists.`)
                  }

                  provider.setErrors([
                    message
                  ])
                })
              } catch (e) {
                Sentry.captureException(e)
                showToaster(this, e.message, { type: 'error' })
              }
            } else {
              const errorMessage = data.error.detail || data.error.title || data.error.message || this.$t('an_error_has_occurred')
              showToaster(this, errorMessage, {
                type: 'error',
                position: 'bottom-right'
              })
            }
          }

          // * exception detail loggin purpose
          Sentry.captureException(
            new Error('Could not accept the bid'),
            {
              extra: {
                err,
                data,
                component: this.$options.name
              }
            }
          )
        }
      },
      /**
       * Accepts the current proposal
       * @function submitted
       * @param {object|null} paymentMethod
       * @param {string} paymentMethod.id
       * @param {string} paymentMethod.type
       */
      submitted (paymentMethod) {
        this.$wait.start(this.loaderKey)
        ShipmentProposal.accept({
          cid: this.getCid,
          sid: this.shipment.uuid,
          pid: this.proposal.uuid
        }, {
          hash: this.proposal.hash || null,
          payment_method: paymentMethod
        })
          .then(this.acceptationSuccessHandler)
          .catch(this.acceptationErrorHandler)
      }
    }
  }
</script>

<style lang="scss">.shipment-proposals-accept-dialog__content {
  padding: 2rem;
  position: relative;
}
.shipment-proposals-accept-dialog__content__title {
  position: relative;
  font-size: 22px;
  left: -8px;
}
.shipment-proposals-accept-dialog__content__title span, .shipment-proposals-accept-dialog__content__title .ctk-font {
  margin: auto 0;
}
.shipment-proposals-accept-dialog__content__title .ctk-font {
  color: $proposal-pending;
}
.shipment-proposals-accept-dialog .modal-wrapper {
  justify-content: unset;
  align-items: unset;
}
.shipment-proposals-accept-dialog .modal-container {
  max-height: initial;
  margin: auto !important;
  width: 100%;
}</style>
